import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import staticGameData from '../data/GameData';
import FontKeys from '../FontKeys';
import gameData from '../gameData';
import DropHelpOverlay from '../marbledrop/DropHelpOverlay';
import DropHUD from '../marbledrop/DropHUD';
import DroppingMarble from '../marbledrop/DroppingMarble';
import GameScreenKeyControls from '../marbledrop/GameScreenKeyControls';
import PenguinDropChar from '../marbledrop/PenguinDropChar';
import SoundAssetKeys from '../SoundAssetKeys';
import UIAssetKeys from '../UIAssetKeys';
import MarbleDropResults from './MarbleDropResults';
import LocalizationSoundAssetKeys from '../Localization-SoundAssetKeys';

export default class MarbleDrop extends Phaser.Scene {
  static get KEY() { return 'MarbleDrop'; }

  constructor() {
    super({ key: MarbleDrop.KEY });
  }

  init() {
    this.penguin = null;
    this.penguinRunSound = null;
    this.gameControls = null;

    // Marbles
    this.lastMarbleSpawnTime = 0;
    this.marbleSpawnWaiter = 800;
    this.gimmeCount = 0;
    this.marbles = []; // Falling marbles
    this.collectedMarbles = []; // Marbles in basket
    this.marbleTrash = []; // Marbles tweening off

    // Sumbit equation
    this.equationContainer = null;
    this.equationMarbles = []; // Marbles in equation
    this.equationSymbols = []; // + or =
    this.equationTotalLabel = null;
    this.equationCounter = 0;

    this.currentAddMarble = null;

    // Overlays
    this.hud = null;
    this.tooMuchText = null;
    this.strikeWarningText = null;
    this.btnHelp = null;
    this.helpOverlay = null;
    this.helpSound = null;

    this.btnAdd = null;
    this.btnDrop = null;

    this.active = true; // Turn inactive while submitting

    // Data
    this.score = 0;
    this.goalNumber = 3;
    this.submittedNumber = null;

    this.correctCount = 0;
    this.incorrectCount = 0;

    this.paused = false;
  }

  create() {
    this.bg = this.add.image(0,0,'dropbackground').setOrigin(0);

    this.marbleSpawnWaiter = gameData.MARBLE_SPAWNTIME;

    /** @type {DroppingMarble[]} */
    this.marbles = [];

    /** @type {DroppingMarble[]} */
    this.collectedMarbles = [];
    this.marbleTrash = [];

    /** @type {DroppingMarble[]} */
    this.equationMarbles = [];
    this.equationSymbols = [];

    this.penguin = new PenguinDropChar(this);
    this.penguin.x = 50;
    this.penguin.y = this.scale.height - this.penguin.getBounds().height - 58;
    this.add.existing(this.penguin);

    this.gameControls = new GameScreenKeyControls(this);
    this.gameControls.x = 0;
    this.gameControls.y = 0;
    this.gameControls.on('CONTROL_INPUT', this.handleControlInput, this);
    this.add.existing(this.gameControls);

    this.hud = new DropHUD(this);
    this.hud.y = 10;
    this.add.existing(this.hud);

    this.tooMuchText = ABCyaPhaser.Text.addText(0,0, 58, 0x4B4B4B, FontKeys._BPreplay_Bold, 'That\'s too many marbles!', this).setOrigin(0);
    this.tooMuchText.x = this.scale.width / 2 - this.tooMuchText.width / 2;
    this.tooMuchText.y = this.scale.height / 2 - this.tooMuchText.height / 2;
    this.tooMuchText.alpha = 0;

    this.strikeWarningText = ABCyaPhaser.Text.addText(0,0, 58, 0x4B4B4B, FontKeys._BPreplay_Bold, 'Strike: 0', this).setOrigin(0);
    this.strikeWarningText.x = this.scale.width / 2 - this.strikeWarningText.width / 2;
    this.strikeWarningText.y = this.scale.height / 2 - this.strikeWarningText.height / 2 - 80;
    this.strikeWarningText.alpha = 0;

    this.btnAdd = ABCyaPhaser.Images.addImage(0,0, UIAssetKeys._md_assets_BtnAdd, this).setOrigin(0)
      .setInteractive();
    this.btnAdd.x = this.scale.width - this.btnAdd.width - 30;
    this.btnAdd.y = 145;
    this.btnAdd.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.submitMarbles, this);

    this.btnDrop = ABCyaPhaser.Images.addImage(30, 145, UIAssetKeys._md_assets_BtnMinus, this).setOrigin(0)
      .setInteractive();
    this.btnDrop.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.dropMarbles, this);

    this.btnHelp = ABCyaPhaser.Images.addImage(0,0, UIAssetKeys._mm_common_assets_BtnHelp, this).setOrigin(0)
      .setInteractive();
    this.btnHelp.x = this.scale.width - this.btnHelp.width - 90;
    this.btnHelp.y = 20;
    this.btnHelp.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.showHelp, this);

    this.helpOverlay = new DropHelpOverlay(this);
    this.helpOverlay.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.hideHelp, this);
    this.add.existing(this.helpOverlay);
    this.showHelp(); // start it off with the help screen

    this.penguinRunSound = ABCyaPhaser.Audio.createSound(this);
    ABCyaPhaser.Audio.playSound(SoundAssetKeys._PenguinRun, this.penguinRunSound, 1, true);
    this.penguinRunSound.pause();

    this.events.once(Phaser.Scenes.Events.DESTROY, () => {
      this.penguinRunSound?.destroy();
      this.penguinRunSound = null;

      this.helpSound?.destroy();
      this.helpSound = null;
    });

    this.initNumber();
  }

  /**
   * 
   * @param {string} key 
   * @returns 
   */
  handleControlInput (key) {
    if (!this.active) return;
    if (!key === null || key === undefined) return;

    switch (key) {

      case gameData.KEY_LEFT:
        this.penguin.setVelocity(-1);
        this.penguinRunSound.resume();
        break;

      case gameData.KEY_RIGHT:
        this.penguin.setVelocity(1);
        this.penguinRunSound.resume();
        break;

      case 'STOP_PENGUIN':
        this.penguinRunSound.pause();
        this.penguin.setVelocity(0);
        break;

      default:
        break;
    }
  }

  submitMarbles () {
    if (this.collectedMarbles.length <= 0) return;
    this.dimMarbles(0.2);
    this.paused = true;
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._SlideWhistleSound, this);
    this.active = false;
    this.submittedNumber = 0;

    this.equationContainer = this.add.container(100,120);
    let vdestX; let vdestY; let vchar; let vcharname;
    const rowMax = 5;
    let rowCount = 0;
    let rowIndex = 0;
    const count = this.collectedMarbles.length;
    for (let i = 0; i < count; i++) {
      rowIndex++;
      const m = this.collectedMarbles[i];
      this.submittedNumber += m.numValue;

      const point = ABCyaPhaser.GameObjects.getScreenPosition(m);
      this.penguin.basketContainer.remove(m);

      m.on('MARBLE_ADDED_ANIMATION_OVER', this.addMarbles, this);
      this.equationMarbles.push(m);
      this.equationContainer.add(m);
      m.x = point.x - this.equationContainer.x;
      m.y = point.y - this.equationContainer.y;
      vdestX = rowIndex * 110;
      vdestY = 60 * rowCount;

      m.setupForEquation();
      ABCyaPhaser.Tweens.addTween(m, 400, { x: vdestX + 17, y: vdestY + 17 }, Phaser.Math.Easing.Linear);// + is for the registration point switch

      // Plus or equals
      vcharname = i < count - 1 ? UIAssetKeys._md_assets_DropPlus : UIAssetKeys._md_assets_DropEquals;
      vchar = ABCyaPhaser.Images.addImage(0,0,vcharname, this.equationContainer).setOrigin(0);
      vdestX += 60;
      vchar.x = vdestX;
      vchar.y = vdestY;
      this.equationSymbols.push(vchar);

      if (rowIndex >= rowMax) {
        rowIndex = 0;
        rowCount++;
      }
    }

    this.equationCounter = 0;
    this.equationTotalLabel = ABCyaPhaser.Text.addText(vdestX + 60, vdestY - 15, 44, 0xffffff, FontKeys._BPreplay_Bold, '', this.equationContainer, false).setOrigin(0);
    const markname = this.goalNumber === this.submittedNumber ? UIAssetKeys._md_assets_DropCorrectMark : UIAssetKeys._md_assets_DropIncorrectMark;
    this.equationMark = ABCyaPhaser.Images.addImage(this.equationTotalLabel.x + 60,this.equationTotalLabel.y + 12,markname, this.equationContainer).setOrigin(0);
    this.equationMark.alpha = 0;

    this.collectedMarbles = [];
    this.currentAddMarble = 0;

    this.time.delayedCall(400, this.addMarbles, null, this);
  }

  dimMarbles (alphavalue) {
    const len = this.marbles.length - 1;
    for (let i = len; i >= 0; i--) {
      const m = this.marbles[i];
      m.alpha = alphavalue;
    }
  }

  addMarbles () {
    if (this.currentAddMarble < this.equationMarbles.length) {
      this.equationCounter += this.equationMarbles[this.currentAddMarble].numValue;
      this.equationTotalLabel.text = this.equationCounter;

      this.equationMarbles[this.currentAddMarble].pulse();
      this.currentAddMarble++;
    }
    else { // Done adding
      ABCyaPhaser.Tweens.addTween(this.equationMark, 400, { alpha: 1 }, Phaser.Math.Easing.Linear, () => this.addingComplete());

      if (this.submittedNumber === this.goalNumber) {
        this.score += this.submittedNumber;
        this.hud.scoreText.text = this.score;
        this.correctCount++;
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._LevelUp, this);
      }
      else {
        this.incorrectCount++;
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._IncorrectMark, this);
      }
    }
  }

  addingComplete () {
    ABCyaPhaser.Tweens.addTween(this.equationMark, 400, { alpha: 1, delay: 1000 }, Phaser.Math.Easing.Linear, () => this.destroyEquation());
  }

  destroyEquation () {
    this.paused = false;
    this.dimMarbles(1);

    this.equationContainer.destroy();
    this.equationContainer = null;
    this.equationMarbles = [];
    this.equationSymbols = [];

    // Check for game over
    if (this.checkGameOver(true)) return; // If we are over get out
    if (this.goalNumber === this.submittedNumber) {
      this.submittedNumber = 0;
      this.initNumber();
    }
    else {
      this.strikeWarningText.text = `Strike: ${this.incorrectCount}`;
      this.tweens.killTweensOf(this.strikeWarningText);
      ABCyaPhaser.Tweens.addTween(this.strikeWarningText, 400, { alpha: 1 }, Phaser.Math.Easing.Linear, () => this.fadeOutStrike());
    }

    this.active = true;
  }

  checkGameOver (doit) {
    let isgameover = false;

    if (this.incorrectCount >= 3) {
      if (doit === true) this.doGameOver();
      isgameover = true;
    }
    return isgameover;
  }

  doGameOver () {
    // Show the last strike
    this.strikeWarningText.text = `Strike: ${this.incorrectCount}`;
    this.tweens.killTweensOf(this.strikeWarningText);
    ABCyaPhaser.Tweens.addTween(this.strikeWarningText, 800, { alpha: 1 }, Phaser.Math.Easing.Linear, () => this.reallyDoGameOver());
  }

  reallyDoGameOver () {
    const gamedata = { score: this.score, correct: this.correctCount, incorrect: this.incorrectCount };
    staticGameData.dropData = gamedata;

    this.scene.start(MarbleDropResults.KEY);
  }

  initNumber () {
    this.goalNumber = Phaser.Math.Between(2,gameData.MAX_MARBLE_VALUE);
    this.hud.goalText.text = this.goalNumber;
  }

  fadeOutStrike () {
    this.tweens.killTweensOf(this.strikeWarningText);
    ABCyaPhaser.Tweens.addTween(this.strikeWarningText, 400, { alpha: 0, delay: 800 }, Phaser.Math.Easing.Linear);
  }

  dropMarbles () {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._RemovedSound, this);

    this.penguin.dropMarbles();
    for (const item in this.collectedMarbles) {
      const m = this.collectedMarbles[item];

      const point = ABCyaPhaser.GameObjects.getScreenPosition(m);
      this.penguin.basketContainer.remove(m);
      this.add.existing(m);
      this.children.bringToTop(m);
      m.x = point.x;
      m.y = point.y;
      ABCyaPhaser.Tweens.addTween(m, 500, { x: m.x - 160, y: this.scale.height + 50 }, Phaser.Math.Easing.Linear, () => this.destroyMarble(m));

      this.marbleTrash.push(m);
    }
    this.collectedMarbles = [];
  }

  destroyMarble (marble) {
    const mIndex = this.marbleTrash.indexOf(marble);
    this.marbleTrash.splice(mIndex,1);
    marble.destroy();
  }

  showHelp () {
    this.children.bringToTop(this.helpOverlay);
    this.helpOverlay.setVisible(true);
    this.helpSound = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    gameData.GAME_MODE === 'DROP' ? ABCyaPhaser.Audio.playSound(LocalizationSoundAssetKeys._MarbleDropInstructionsDrop, this.helpSound) : ABCyaPhaser.Audio.playSound(LocalizationSoundAssetKeys._MarbleDropInstructions, this.helpSound);
    this.paused = true;
  }

  hideHelp () {
    this.helpOverlay.setVisible(false);
    this.helpSound.destroy();
    this.helpSound = null;
    this.paused = false;
  }

  /**
   * 
   * @param {Phaser.GameObjects.Container} obj1 
   * @param {DroppingMarble} obj2 
   * @returns 
   */
  checkCollision (obj1, obj2) {
    const isHit = false;
    const b1 = obj1.getBounds();
    const b2 = obj2.getBounds();

    return Phaser.Geom.Rectangle.Overlaps(b1, b2);
  }

  /**
   * 
   * @param {DroppingMarble} item 
   */
  collectMarble (item) {
    const rowIndex = this.collectedMarbles.length < 3 ? this.collectedMarbles.length : this.collectedMarbles.length % 3;
    const rowNum = Math.floor(this.collectedMarbles.length / 3);
    item.setPosition(30 * rowIndex, rowNum * -30);
    this.penguin.basketContainer.add(item);
    this.collectedMarbles.push(item);
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._PlacedSound, this);
    this.checkForTooMuch();
  }

  checkForTooMuch () {
    const count = this.collectedMarbles.length;
    let total = 0;
    for (let i = 0; i < count; i++) {
      total += this.collectedMarbles[i].numValue;
    }
    if (total > this.goalNumber) {
      // Forced dropped counts as incorrecxt
      this.incorrectCount++;
      this.dropMarbles();
      this.tweens.killTweensOf(this.tooMuchText);
      ABCyaPhaser.Tweens.addTween(this.tooMuchText, 400, { alpha: 400 }, Phaser.Math.Easing.Linear, () => this.handleTooMuchWait());
      this.strikeWarningText.text = `Strike: ${this.incorrectCount}`;
      this.tweens.killTweensOf(this.strikeWarningText);
      ABCyaPhaser.Tweens.addTween(this.strikeWarningText, 400, { alpha: 400 }, Phaser.Math.Easing.Linear, () => this.fadeOutStrike());
    }
  }

  handleTooMuchWait () {
    this.checkGameOver(true);
    this.tweens.killTweensOf(this.tooMuchText);
    ABCyaPhaser.Tweens.addTween(this.tooMuchText, 400, { alpha: 0, delay: 400 }, Phaser.Math.Easing.Linear);
  }

  spawnMarble () {
    let maxNum = gameData.MAX_MARBLE_VALUE;
    if (this.gimmeCount > 4) {
      maxNum = this.goalNumber;
      this.gimmeCount = 0;
    }
    else {
      this.gimmeCount++;
    }
    const item = new DroppingMarble(this, maxNum);
    item.x = Phaser.Math.Between(85, this.scale.width - 120);
    item.y = -40;
    this.marbles.push(item);
    this.add.existing(item);
  }

  update(time, delta) {
    if (this.paused) return; // Skip if we are paused
    // Update penguin
    this.penguin.tick(delta);

    // Marble check
    const len = this.marbles.length - 1;
    for (let i = len; i >= 0; i--) {
      // Check collision
      const m = this.marbles[i];
      if (m.y > 300 && this.active) {
        const hit = this.checkCollision(this.penguin.basketHit, m);
        if (hit) {
          this.marbles.splice(i,1);
          this.collectMarble(m);
          continue;
        }
        else {
          m.tick(delta);
        }
      }
      else {
        m.tick(delta);
      }

      // Check for bottom of screen
      if (m.y >= this.scale.height - 30) {
        this.marbles.splice(i,1);
        this.marbleTrash.push(m);
        ABCyaPhaser.Tweens.addTween(m, 400, { alpha: 0 }, Phaser.Math.Easing.Linear, () => this.destroyMarble(m));
      }
    }

    // Spawn marble
    if (time - this.lastMarbleSpawnTime > this.marbleSpawnWaiter) {
      this.spawnMarble();
      this.lastMarbleSpawnTime = time;
    }
  }
}